<script>
  // Re-picks the poster frame of an already-uploaded video moment. The frame is
  // captured from the CDN copy of the clip and pushed through the same prepare →
  // presigned PUT → finalize pipeline the original thumbnail used.
  //
  //   <ChangeVideoThumbnailModal target={{ momentId, videoUrl, thumbnailUrl }} reload={['quests']} onchanged={…} onclose={…} />
  //
  // `reload` names the host page's props to refresh once the new frame is saved.
  import { router } from '@inertiajs/svelte'
  import Modal from '../modal.svelte'
  import VideoThumbnailPicker from './video_thumbnail_picker.svelte'
  import { Button } from '../ui'
  import { requestPreparedUpload, uploadPrepared } from '../../lib/presigned_upload.js'

  let { target = null, reload = [], onchanged, onclose } = $props()

  let thumbnailBlob = $state(null)
  let saving = $state(false)
  let uploadProgress = $state(null)
  let error = $state(null)

  $effect(() => {
    // A new target starts clean; the picker emits its first pick on load.
    target
    thumbnailBlob = null
    error = null
  })

  // prepare (the `prepared_upload` prop — lib/presigned_upload.js) → presigned PUT.
  const upload = () =>
    uploadPrepared(() => requestPreparedUpload(`/moments/${target.momentId}/video-thumbnail/prepare`), { thumbnail: thumbnailBlob }, {
      onProgress: (fraction) => (uploadProgress = fraction)
    })

  const save = async () => {
    if (!thumbnailBlob) {
      error = { title: 'Pick a frame first.' }
      return
    }
    saving = true
    error = null
    uploadProgress = 0
    let mediaUploadId
    try {
      mediaUploadId = await upload()
    } catch (e) {
      error = { title: 'Thumbnail not saved', text: e.message }
      saving = false
      uploadProgress = null
      return
    }

    router.patch(
      `/moments/${target.momentId}/video-thumbnail`,
      { media_upload_id: mediaUploadId },
      {
        preserveScroll: true,
        preserveState: true,
        only: [...reload, 'flash', 'errors'],
        onSuccess: () => onchanged?.(),
        onError: (errors) => {
          error = {
            title: 'Thumbnail not saved',
            text:
              errors.thumbnail_code === 'ExpiredMediaUploadError'
                ? 'The upload expired before it could be saved — click Save thumbnail to retry.'
                : errors.thumbnail
          }
        },
        onFinish: () => {
          saving = false
          uploadProgress = null
        }
      }
    )
  }
</script>

<!-- Closing mid-save would orphan the prepared upload, so the modal is locked while saving. -->
<Modal open={!!target} title="Change video thumbnail" dismissible={!saving} {onclose}>
  {#if target}
    <p class="text-sm text-base-content/60 mb-2">
      Scrub through the clip and pick the frame shown as this post's poster in the app. The video itself is not
      changed.
    </p>

    {#key target.videoUrl}
      <VideoThumbnailPicker
        source={target.videoUrl}
        currentThumbnailUrl={target.thumbnailUrl}
        disabled={saving}
        onpick={({ blob }) => (thumbnailBlob = blob)}
        onerror={(e) => (error = { title: 'Could not read the video', text: e.message })}
      />
    {/key}

    {#if uploadProgress !== null}
      <div class="mt-4">
        <progress class="progress progress-primary w-full" value={Math.round(uploadProgress * 100)} max="100"></progress>
        <p class="text-xs text-base-content/60">Uploading thumbnail… {Math.round(uploadProgress * 100)}%</p>
      </div>
    {/if}

    {#if error}
      <div class="alert alert-error alert-soft mt-4 text-sm" role="alert">
        <div>
          <div class="font-semibold">{error.title}</div>
          {#if error.text}<div class="whitespace-pre-line">{error.text}</div>{/if}
        </div>
      </div>
    {/if}

    <div class="flex justify-end gap-2 mt-4">
      <Button variant="ghost" disabled={saving} onclick={() => onclose?.()}>Cancel</Button>
      <Button variant="primary" loading={saving} disabled={!thumbnailBlob || saving} onclick={save}>Save thumbnail</Button>
    </div>
  {/if}
</Modal>
